// ── Panel de plataforma — detalle de cuenta (2026-08-17) ─────────────────────
// Detail Page Pattern: DetailSection + KVRow + SectionHeaderAction. Los únicos
// botones válidos en titleRight son SectionHeaderAction (REGLA #15).

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_MODULES, blockingDependents, missingDependencies,
  type AccountModuleKey, type AccountModules, type AccountStatus,
} from '@evo-hub/shared';
import { DetailSection } from '../components/ui/DetailSection.js';
import { Field } from '../components/ui/Field.js';
import { KVRow } from '../components/ui/KVRow.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { Toggle } from '../components/ui/Toggle.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Pill } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { parseServerDate } from '../lib/date.js';
import { ApiError } from '../lib/api.js';
import {
  usePlatformAccount, usePlatformAccountPayments, useChangeAccountStatus,
  useUpdateAccountLimits,
  useSetAccountTrial, useUpdateAccountModules, useCancelAccountSubscription,
  useAccountUsageMonths, useChargeUsageMonth, usePuenteSuscripcion, useSetPuenteEncendido,
  type UsageMonth,
} from '../hooks/usePlatformAccounts.js';

const STATUS_TONE: Record<AccountStatus, 'success' | 'amber' | 'danger' | 'muted'> = {
  active: 'success', provisioning: 'muted', suspended: 'amber', closed: 'danger',
};

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return parseServerDate(iso).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtMoney(cents: number | null, currency: string | null): string | null {
  if (cents == null) return null;
  return new Intl.NumberFormat('es', { style: 'currency', currency: (currency ?? 'usd').toUpperCase() }).format(cents / 100);
}

function fmtUsd(value: number | null): string | null {
  if (value == null) return null;
  return new Intl.NumberFormat('es', { style: 'currency', currency: 'USD' }).format(value);
}

export function PlatformAccountDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { setOverride } = useTopBarCtx();

  const { data: account, isLoading } = usePlatformAccount(id);
  const { data: payments, isLoading: paymentsLoading } = usePlatformAccountPayments(id);

  useEffect(() => {
    setOverride({
      title: account?.name ?? t('platformAdmin.accountsTitle'),
      ...(account?.id ? { subtitle: account.id } : {}),
    });
    return () => setOverride({});
  }, [account, t, setOverride]);

  if (isLoading) return <LoadingState fullHeight />;
  if (!account) {
    return (
      <EmptyState
        title={t('common.notFound', 'No se encontró')}
        action={<Button variant="outline" tone="muted" onClick={() => navigate('/admin/cuentas')}>{t('platformAdmin.backToAccounts')}</Button>}
      />
    );
  }

  const dash = t('platformAdmin.noData');

  return (
    // AppShell monta <main className="flex-1 overflow-hidden"> — cada página
    // scrollea sola. Sin height:100% el overflow:auto no tiene contra qué
    // desbordar y el contenido queda recortado por el overflow:hidden del
    // padre: la sección de Módulos se cortaba después de Contactos y Email,
    // sin barra para llegar al resto.
    <div style={{ height: '100%', overflowY: 'auto', padding: 'var(--evo-space-6) var(--evo-page-pad-x)' }}>
      {/* Dos columnas que apilan tarjetas a su altura natural (.evo-duo-stack,
          2026-10-12). Antes eran filas de dos a altura pareja: consumo (largo)
          al lado de pagos (corto) dejaba una caja grande y vacía. Izquierda:
          la cuenta y lo que gasta. Derecha: cómo paga. Abajo, a todo el
          ancho, lo que necesita ancho (meses, módulos, usuarios, riesgo). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <div className="evo-duo-stack">
      <div>
        <DetailSection title={t('platformAdmin.detail.accountCard')} divider={false}>
          <KVRow label={t('platformAdmin.detail.accountName', { defaultValue: 'Nombre' })} value={account.name} />
          <KVRow label={t('platformAdmin.detail.accountId')} value={<span style={{ fontFamily: 'var(--evo-font-mono)' }}>{account.id}</span>} />
          <KVRow label={t('platformAdmin.detail.createdAt')} value={fmtDate(account.createdAt) ?? dash} />
          <KVRow
            label={t('platformAdmin.detail.status')}
            value={<Pill size="sm" variant="outline" tone={STATUS_TONE[account.status]}>{t(`platformAdmin.status.${account.status}`)}</Pill>}
            last
          />
        </DetailSection>
        <UsageSection account={account} />
      </div>

      <div>
        <DetailSection title={t('platformAdmin.detail.subscriptionCard')} divider={false}>
          <KVRow
            label={t('platformAdmin.detail.subscriptionStatus')}
            value={
              <Pill size="sm" variant="outline" tone={account.billingExempt ? 'muted' : account.subscriptionStatus === 'active' ? 'success' : account.subscriptionStatus === 'grace_period' ? 'amber' : 'danger'}>
                {account.billingExempt ? t('platformAdmin.subscription.exempt') : t(`platformAdmin.subscription.${account.subscriptionStatus}`)}
              </Pill>
            }
          />
          {account.gracePeriodStartedAt && (
            <KVRow label={t('platformAdmin.detail.graceUntil')} value={fmtDate(account.gracePeriodStartedAt) ?? dash} />
          )}
          <TrialRow account={account} />
          <PriceRow account={account} />
          <KVRow label={t('platformAdmin.detail.customerId')} value={account.stripeCustomerId ? <span style={{ fontFamily: 'var(--evo-font-mono)' }}>{account.stripeCustomerId}</span> : dash} last />
        </DetailSection>
        <PaymentsSection loading={paymentsLoading} payments={payments} />
        <PuenteSection accountId={account.id} />
      </div>
      </div>

      <UsageMonthsSection account={account} payments={payments} />

      <ModulesSection account={account} />

      <UsersSection account={account} />

      {/* Zona de riesgo: SIEMPRE la última tarjeta, a ancho completo, nunca
          una tarjeta más de la rejilla. */}
      <DangerSection account={account} />
      </div>
    </div>
  );
}

// ── Plan / price_id de Stripe (2026-08-18) ───────────────────────────────────
// Editable SOLO acá. Hasta hoy el único setter vivía en los Ajustes del propio
// cliente, con dos consecuencias: una cuenta sin price_id no podía pagar (el
// botón salía deshabilitado y nadie sabía por qué), y un owner podía apuntarse
// a un precio más barato.
function PriceRow({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const updateLimits = useUpdateAccountLimits();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  async function save(priceId: string | null) {
    try {
      await updateLimits.mutateAsync({ id: account.id, stripePriceId: priceId });
      toast.success(t('platformAdmin.price.saved'));
      setOpen(false);
    } catch (err) { toast.error(err instanceof ApiError ? err.message : String(err)); }
  }

  return (
    <>
      <KVRow
        label={t('platformAdmin.price.exceptionLabel')}
        value={
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            {/* Desde el catálogo de planes (2026-08-19) esto es la EXCEPCIÓN,
                no el camino normal: vacío significa "usa el plan que elija el
                cliente", no "no puede pagar". */}
            {account.stripePriceId
              ? <span>{account.stripePriceId}</span>
              : <span style={{ color: 'var(--evo-fg-3)' }}>{t('platformAdmin.price.noException')}</span>}
            <SectionHeaderAction onClick={() => { setDraft(account.stripePriceId ?? ''); setOpen(true); }}>
              {t('common.edit', 'Editar')}
            </SectionHeaderAction>
          </span>
        }
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('platformAdmin.detail.priceId')}
        size="sm"
        footer={
          <>
            {account.stripePriceId && (
              <Button variant="outline" tone="danger" loading={updateLimits.isPending} onClick={() => { void save(null); }}>
                {t('platformAdmin.price.clearBtn')}
              </Button>
            )}
            <Button variant="outline" tone="muted" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" loading={updateLimits.isPending} onClick={() => { void save(draft.trim()); }}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Field label={t('platformAdmin.price.label')} hint={t('platformAdmin.price.exceptionHint')}>
          <Input value={draft} onChange={setDraft} placeholder="price_..." fullWidth autoFocus />
        </Field>
      </Modal>
    </>
  );
}

// ── Período de prueba (2026-08-18) ───────────────────────────────────────────
// Se concede en días desde hoy. No crea nada en Stripe ni cambia el estado de
// la suscripción: la cuenta sigue formalmente 'suspended' y lo que la deja
// trabajar es la fecha, así que cuando vence no hay estado que revertir.
function TrialRow({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const setTrial = useSetAccountTrial();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('30');

  const active = account.trialEndsAt != null && new Date(account.trialEndsAt).getTime() > Date.now();
  const daysLeft = account.trialEndsAt
    ? Math.ceil((new Date(account.trialEndsAt).getTime() - Date.now()) / 86_400_000)
    : 0;

  async function save(days: number | null) {
    try {
      await setTrial.mutateAsync({ id: account.id, days });
      setOpen(false);
    } catch (err) { toast.error(err instanceof ApiError ? err.message : String(err)); }
  }

  async function grant() {
    const parsed = Number(draft.trim());
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 365) {
      toast.error(t('platformAdmin.trial.daysLabel'));
      return;
    }
    await save(Math.round(parsed));
  }

  return (
    <>
      <KVRow
        label={t('platformAdmin.trial.title')}
        value={
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            {account.trialEndsAt == null ? (
              <span style={{ color: 'var(--evo-fg-3)' }}>{t('platformAdmin.trial.none')}</span>
            ) : (
              <Pill size="sm" variant="outline" tone={active ? (daysLeft <= 7 ? 'amber' : 'cyan') : 'danger'}>
                {active
                  ? t('platformAdmin.trial.activeUntil', { date: fmtDate(account.trialEndsAt), days: daysLeft })
                  : t('platformAdmin.trial.expiredOn', { date: fmtDate(account.trialEndsAt) })}
              </Pill>
            )}
            <SectionHeaderAction onClick={() => { setDraft('30'); setOpen(true); }}>
              {t('common.edit', 'Editar')}
            </SectionHeaderAction>
          </span>
        }
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('platformAdmin.trial.title')}
        size="sm"
        footer={
          <>
            {account.trialEndsAt != null && (
              <Button variant="outline" tone="danger" loading={setTrial.isPending} onClick={() => { void save(null); }}>
                {t('platformAdmin.trial.revokeBtn')}
              </Button>
            )}
            <Button variant="outline" tone="muted" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" loading={setTrial.isPending} onClick={() => { void grant(); }}>
              {t('platformAdmin.trial.grantBtn')}
            </Button>
          </>
        }
      >
        <Field label={t('platformAdmin.trial.daysLabel')} hint={t('platformAdmin.trial.hint')}>
          <Input value={draft} onChange={setDraft} type="number" min={1} max={365} fullWidth autoFocus />
        </Field>
      </Modal>
    </>
  );
}

// ── Pagos ────────────────────────────────────────────────────────────────────

function PaymentsSection({ loading, payments }: { loading: boolean; payments: ReturnType<typeof usePlatformAccountPayments>['data'] }) {
  const { t } = useTranslation();
  const dash = t('platformAdmin.noData');

  return (
    <DetailSection title={t('platformAdmin.detail.paymentsSection')}>
      {loading && <LoadingState size="sm" />}
      {!loading && !payments?.hasCustomer && (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
          {t('platformAdmin.detail.noCustomer')}
        </span>
      )}
      {!loading && payments?.hasCustomer && (
        <>
          <KVRow
            label={t('platformAdmin.detail.nextCharge')}
            value={payments.nextChargeAt
              ? `${fmtMoney(payments.nextChargeAmount, payments.nextChargeCurrency) ?? ''} · ${fmtDate(payments.nextChargeAt)}`.trim()
              : dash}
          />
          <KVRow
            label={t('platformAdmin.detail.paymentMethod')}
            value={payments.paymentMethod ? `${payments.paymentMethod.brand} ····${payments.paymentMethod.last4}` : dash}
          />
          {payments.invoices.length === 0 && (
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
              {t('platformAdmin.detail.noPayments')}
            </span>
          )}
          {payments.invoices.map(inv => (
            <KVRow
              key={inv.id}
              label={fmtDate(inv.date) ?? dash}
              value={
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                  {fmtMoney(inv.amountPaid, inv.currency)}
                  {inv.status && <Pill size="xs" tone={inv.status === 'paid' ? 'success' : 'muted'}>{inv.status}</Pill>}
                  {inv.hostedInvoiceUrl && (
                    <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--evo-cyan)', fontSize: 'var(--evo-fs-caption)' }}>
                      {t('platformAdmin.detail.viewInvoice')}
                    </a>
                  )}
                </span>
              }
            />
          ))}
        </>
      )}
    </DetailSection>
  );
}

// ── Consumo por mes y su factura (2026-10-05) ────────────────────────────────
// Un mes por fila, del más reciente al más viejo. Un mes cerrado sin cargo se
// factura desde acá: la línea entra en la PRÓXIMA factura de la suscripción en
// Stripe, la misma que ya paga el cliente cada mes. Es plata que le llega a un
// cliente, así que pasa siempre por el modal con el monto a la vista.

function etiquetaMes(mes: string, lang: string): string {
  const s = new Date(`${mes}-01T00:00:00Z`).toLocaleDateString(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function UsageMonthsSection({ account, payments }: {
  account: NonNullable<ReturnType<typeof usePlatformAccount>['data']>;
  payments: ReturnType<typeof usePlatformAccountPayments>['data'];
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.language;
  const { data, isLoading } = useAccountUsageMonths(account.id);
  const charge = useChargeUsageMonth();
  const [confirmando, setConfirmando] = useState<UsageMonth | null>(null);

  const usd = (v: number) => new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD' }).format(v);
  const compacto = (v: number) => new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: 1 }).format(v);
  const caption = { fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' } as const;

  async function facturar() {
    if (!confirmando) return;
    const mes = confirmando.mes;
    try {
      await charge.mutateAsync({ id: account.id, mes });
      toast.success(t('platformAdmin.detail.usageMonths.done', { month: etiquetaMes(mes, lang) }));
      setConfirmando(null);
    } catch (err) {
      const texto = err instanceof Error ? err.message : String(err);
      const code = err instanceof ApiError ? err.code : undefined;
      toast.error(code ? t(`platformAdmin.detail.usageMonths.errors.${code}`, { defaultValue: texto }) : texto);
    }
  }

  function estado(m: UsageMonth) {
    if (m.cargo && !m.cargo.porCiclo) {
      return (
        <>
          <Pill size="sm" variant="outline" tone="success">{t('platformAdmin.detail.usageMonths.billed')}</Pill>
          {!m.cargo.enStripe && <Pill size="sm" variant="outline" tone="amber">{t('platformAdmin.detail.usageMonths.notInStripe')}</Pill>}
          <span style={caption}>{t('platformAdmin.detail.usageMonths.billedOn', { date: fmtDate(m.cargo.registradoEn) })}</span>
        </>
      );
    }
    if (m.cargo) return <Pill size="sm" variant="outline" tone="muted">{t('platformAdmin.detail.usageMonths.billedByCycle')}</Pill>;
    if (!m.cerrado) return <Pill size="sm" variant="outline" tone="muted">{t('platformAdmin.detail.usageMonths.current')}</Pill>;
    if (account.billingExempt) return <Pill size="sm" variant="outline" tone="muted">{t('platformAdmin.detail.usageMonths.exempt')}</Pill>;
    if (!account.stripeCustomerId) return <Pill size="sm" variant="outline" tone="muted">{t('platformAdmin.detail.usageMonths.noCustomer')}</Pill>;
    return (
      <>
        <Pill size="sm" variant="outline" tone="amber">{t('platformAdmin.detail.usageMonths.toBill')}</Pill>
        <SectionHeaderAction onClick={() => setConfirmando(m)}>{t('platformAdmin.detail.usageMonths.bill')}</SectionHeaderAction>
      </>
    );
  }

  const meses = data?.months ?? [];

  return (
    <DetailSection title={t('platformAdmin.detail.usageMonths.title')}>
      {isLoading && <LoadingState />}
      {!isLoading && meses.length === 0 && (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>{t('platformAdmin.detail.usageMonths.empty')}</span>
      )}
      {meses.map((m, i) => (
        <KVRow
          key={m.mes}
          label={etiquetaMes(m.mes, lang)}
          last={i === meses.length - 1}
          value={
            <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
              <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
                {/* Cuenta exenta: el monto ×multiplicador no se cobra nunca y
                    solo confunde. Se muestra lo que costó de verdad. */}
                <strong>{usd(account.billingExempt ? m.brutoUsd : m.montoCents / 100)}</strong>
                {!account.billingExempt && (
                  <span style={caption}>
                    {t('platformAdmin.detail.usageMonths.calc', { billable: usd(m.cobrableUsd), multiplier: `${m.multiplicador}×` })}
                  </span>
                )}
                {estado(m)}
              </span>
              {/* Qué se usó, no solo cuánto: un servicio en $0 (el plan, un
                  cupo gratuito) se muestra por cantidad. */}
              {m.porServicio.length > 0 && (
                <span style={caption}>
                  {m.porServicio.slice(0, 5)
                    .map(s => `${s.etiqueta}: ${s.brutoUsd > 0 ? usd(s.brutoUsd) : compacto(s.cantidad)}`)
                    .join(' · ')}
                </span>
              )}
            </span>
          }
        />
      ))}

      <Modal
        open={confirmando != null}
        onClose={() => setConfirmando(null)}
        title={confirmando ? t('platformAdmin.detail.usageMonths.modalTitle', { month: etiquetaMes(confirmando.mes, lang) }) : ''}
        size="sm"
        footer={
          <>
            <Button variant="outline" tone="muted" onClick={() => setConfirmando(null)}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" loading={charge.isPending} onClick={() => { void facturar(); }}>
              {t('platformAdmin.detail.usageMonths.confirm')}
            </Button>
          </>
        }
      >
        {confirmando && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <div>
              <KVRow label={t('platformAdmin.detail.usageMonths.modalBillable')} value={usd(confirmando.cobrableUsd)} />
              <KVRow label={t('platformAdmin.detail.usageMonths.modalMultiplier')} value={`${confirmando.multiplicador}×`} />
              <KVRow label={t('platformAdmin.detail.usageMonths.modalTotal')} value={<strong>{usd(confirmando.montoCents / 100)}</strong>} last />
            </div>
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
              {t('platformAdmin.detail.usageMonths.modalNote')}
              {payments?.nextChargeAt ? ` ${t('platformAdmin.detail.usageMonths.modalNext', { date: fmtDate(payments.nextChargeAt) })}` : ''}
            </span>
          </div>
        )}
      </Modal>
    </DetailSection>
  );
}

// ── Plan de Claude (Mac) — solo en la cuenta operadora ───────────────────────
// Lo que la cuenta operadora le pide a Claude lo ejecuta Claude Code en la Mac
// con el plan (services/puenteSuscripcion/). Esto dice si la Mac está, cuánto
// pasó por el plan y cuánto cayó a la API, y permite apagarlo.

function PuenteSection({ accountId }: { accountId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { data } = usePuenteSuscripcion(true);
  const setEncendido = useSetPuenteEncendido();

  if (!data || data.cuentaOperadora !== accountId) return null;

  const cuando = (ms: number) => new Date(ms).toLocaleString(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const caption = { fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' } as const;
  const alApi = data.ultimoDia.fallido + data.ultimoDia.abandonado;

  async function cambiar(next: boolean) {
    try {
      await setEncendido.mutateAsync(next);
      toast.success(t('platformAdmin.detail.puente.saved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <DetailSection title={t('platformAdmin.detail.puente.title')}>
      <KVRow
        label={t('platformAdmin.detail.puente.status')}
        value={
          <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
            {!data.encendido
              ? <Pill size="sm" variant="outline" tone="muted">{t('platformAdmin.detail.puente.off')}</Pill>
              : data.conectada
                ? <Pill size="sm" variant="outline" tone="success">{t('platformAdmin.detail.puente.connected', { plan: data.latido?.plan ?? '' })}</Pill>
                : <Pill size="sm" variant="outline" tone="danger">{t('platformAdmin.detail.puente.disconnected')}</Pill>}
            <span style={caption}>
              {data.latido ? t('platformAdmin.detail.puente.lastSeen', { when: cuando(data.latido.en) }) : t('platformAdmin.detail.puente.never')}
            </span>
          </span>
        }
      />
      <KVRow
        label={t('platformAdmin.detail.puente.lastDay')}
        value={t('platformAdmin.detail.puente.lastDayValue', { plan: data.ultimoDia.listo, api: alApi })}
      />
      {data.ultimoError && (
        <KVRow
          label={t('platformAdmin.detail.puente.lastError')}
          value={<span style={caption}>{`${cuando(data.ultimoError.en)} · ${data.ultimoError.error}`}</span>}
        />
      )}
      <KVRow
        label={t('platformAdmin.detail.puente.toggle')}
        last
        value={
          <Toggle
            checked={data.encendido}
            onChange={next => { void cambiar(next); }}
            description={t('platformAdmin.detail.puente.toggleHelp')}
            size="sm"
            disabled={setEncendido.isPending}
          />
        }
      />
    </DetailSection>
  );
}

// ── Consumo de API ───────────────────────────────────────────────────────────

function UsageSection({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const updateLimits = useUpdateAccountLimits();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingMultiplier, setEditingMultiplier] = useState(false);
  const [multiplierDraft, setMultiplierDraft] = useState('');
  const dash = t('platformAdmin.noData');

  async function saveMultiplier() {
    const parsed = Number(multiplierDraft.trim());
    if (!Number.isFinite(parsed) || parsed < 0) { toast.error(t('platformAdmin.detail.multiplierInvalid')); return; }
    try {
      await updateLimits.mutateAsync({ id: account.id, usageMultiplier: parsed });
      toast.success(t('platformAdmin.detail.multiplierSaved'));
      setEditingMultiplier(false);
    } catch (err) { toast.error(err instanceof ApiError ? err.message : String(err)); }
  }

  async function saveLimit() {
    const trimmed = draft.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      toast.error(t('platformAdmin.detail.limitPlaceholder'));
      return;
    }
    try {
      await updateLimits.mutateAsync({ id: account.id, monthlyLimitUsd: parsed });
      toast.success(t('platformAdmin.detail.limitSaved'));
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function toggleExempt(next: boolean) {
    try {
      await updateLimits.mutateAsync({ id: account.id, billingExempt: next });
      toast.success(t('platformAdmin.detail.exemptSaved'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <DetailSection
      title={t('platformAdmin.detail.usageSection')}
      titleRight={
        <SectionHeaderAction
          onClick={() => { setDraft(account.monthLimitUsd != null ? String(account.monthLimitUsd) : ''); setEditing(true); }}
        >
          {t('platformAdmin.detail.editLimit')}
        </SectionHeaderAction>
      }
    >
      {/* null = no se pudo leer la DB de la cuenta. Se dice, no se muestra $0. */}
      {/* Medidor con barra: un número suelto no dice si vas holgado o al
          límite. Tono por porcentaje: <70% cian, 70-99% ámbar, 100% rojo. */}
      {account.monthSpendUsd != null && account.monthLimitUsd != null && account.monthLimitUsd > 0 ? (() => {
        const pct = Math.min(100, Math.round(100 * account.monthSpendUsd! / account.monthLimitUsd!));
        const color = pct >= 100 ? 'var(--evo-danger)' : pct >= 70 ? 'var(--evo-amber)' : 'var(--evo-cyan)';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: 'var(--evo-space-3) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-2)' }}>
              <span style={{ flex: 1, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
                {t('platformAdmin.detail.monthSpend')}
                <span style={{ color: 'var(--evo-fg-4)', marginLeft: 'var(--evo-space-2)', fontSize: 'var(--evo-fs-tiny)' }}>
                  {t('platformAdmin.detail.asOf', { date: new Date().toLocaleDateString('es', { day: 'numeric', month: 'short' }) })}
                </span>
              </span>
              <span style={{ fontSize: 'var(--evo-list-title-fs)', color, fontFamily: 'var(--evo-font)' }}>{fmtUsd(account.monthSpendUsd)}</span>
              <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>de {fmtUsd(account.monthLimitUsd)}</span>
            </div>
            <div style={{ height: 4, borderRadius: 'var(--evo-r-pill)', background: 'var(--evo-bg-2)', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color }} />
            </div>
          </div>
        );
      })() : (
        <KVRow
          label={t('platformAdmin.detail.monthSpend')}
          value={account.monthSpendUsd != null ? fmtUsd(account.monthSpendUsd) : t('platformAdmin.detail.usageUnavailable')}
        />
      )}
      {/* Bruto ≠ cobrable. El bruto es lo que se llamó; lo cobrable es lo que
          EvoHub de verdad pagó. Facturar el bruto sería cobrar plata que nunca
          salió de la cuenta de Google. */}
      {account.monthCoveredUsd != null && account.monthCoveredUsd > 0 && (
        <KVRow
          label={t('platformAdmin.detail.monthCovered')}
          value={<span style={{ color: 'var(--evo-success)' }}>−{fmtUsd(account.monthCoveredUsd)}</span>}
        />
      )}
      {account.monthBillableUsd != null && (
        <KVRow
          label={t('platformAdmin.detail.monthBillable')}
          value={<strong>{fmtUsd(account.monthBillableUsd)}</strong>}
        />
      )}
      <KVRow
        label={t('platformAdmin.detail.monthLimit')}
        value={
          <>
            {account.monthLimitUsd != null ? fmtUsd(account.monthLimitUsd) : t('platformAdmin.detail.noLimit')}
            <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{t('platformAdmin.detail.limitNote')}</span>
          </>
        }
      />
      {/* Multiplicador y encendido del cobro por consumo. El encendido nunca
          acepta una fecha pasada — el backend lo rechaza con 400, y acá el
          campo arranca vacío para que nadie lo tipee por inercia. */}
      <KVRow
        label={t('platformAdmin.detail.usageMultiplier')}
        value={
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            <strong>{account.usageMultiplier}×</strong>
            {Number(account.usageMultiplier) === 1 && (
              <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{t('platformAdmin.detail.multiplierNoSurcharge')}</span>
            )}
            <SectionHeaderAction
              onClick={() => { setMultiplierDraft(String(account.usageMultiplier)); setEditingMultiplier(true); }}
            >
              {t('common.edit', 'Editar')}
            </SectionHeaderAction>
          </span>
        }
      />
      <KVRow
        label={t('platformAdmin.detail.usageBillingSince')}
        value={account.usageBillingSince
          ? fmtDate(account.usageBillingSince)
          : <span style={{ color: 'var(--evo-fg-3)' }}>{t('platformAdmin.detail.usageBillingOff')}</span>}
      />
      <KVRow
        label={t('platformAdmin.detail.billingExempt')}
        value={
          <Toggle
            checked={account.billingExempt}
            onChange={next => { void toggleExempt(next); }}
            description={t('platformAdmin.detail.billingExemptHelp')}
            size="sm"
            disabled={updateLimits.isPending}
          />
        }
      />

      {account.creditsByService.length === 0 && (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
          {t('platformAdmin.detail.noUsage')}
        </span>
      )}
      {account.creditsByService.map(row => (
        <KVRow
          key={row.service}
          label={row.label}
          value={
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                <strong>{fmtUsd(row.billableCostUsd) ?? dash}</strong>
                {row.coveredCostUsd > 0 && (
                  <span style={{ color: 'var(--evo-fg-3)' }}>
                    {' '}({t('platformAdmin.detail.ofGross', { gross: fmtUsd(row.grossCostUsd) })})
                  </span>
                )}
              </span>
              {/* El reparto se explica en la fila. Si el cliente pregunta por
                  qué le tocó ese crédito, la respuesta está a la vista: cuánto
                  usó él sobre cuánto se usó en total, contra el cupo. */}
              {row.tierQuantity != null ? (
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {t('platformAdmin.detail.creditShare', {
                    used: new Intl.NumberFormat('es').format(Math.round(row.grossQuantity)),
                    free: new Intl.NumberFormat('es').format(Math.round(row.freeQuantity)),
                    tier: new Intl.NumberFormat('es').format(row.tierQuantity),
                    platform: new Intl.NumberFormat('es').format(Math.round(row.platformQuantity)),
                  })}
                </span>
              ) : (
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {/* Un cupo infinito (el plan de la Mac, Composio, PageSpeed)
                      llega como null —Infinity no viaja en JSON— y se leía
                      "sin cupo, se cobra entero" sobre algo en $0. */}
                  {t(row.grossCostUsd === 0 ? 'platformAdmin.detail.noUseCost' : 'platformAdmin.detail.noCredit', {
                    used: new Intl.NumberFormat('es').format(Math.round(row.grossQuantity)),
                  })}
                </span>
              )}
            </span>
          }
        />
      ))}

      <Modal
        open={editingMultiplier}
        onClose={() => setEditingMultiplier(false)}
        title={t('platformAdmin.detail.usageMultiplier')}
        size="sm"
        footer={
          <>
            <Button variant="outline" tone="muted" onClick={() => setEditingMultiplier(false)}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" loading={updateLimits.isPending} onClick={() => { void saveMultiplier(); }}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Input value={multiplierDraft} onChange={setMultiplierDraft} type="number" min={0}
          label={t('platformAdmin.detail.multiplierHelp')} fullWidth autoFocus />
      </Modal>

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title={t('platformAdmin.detail.editLimit')}
        size="sm"
        footer={
          <>
            <Button variant="outline" tone="muted" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" loading={updateLimits.isPending} onClick={() => { void saveLimit(); }}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Input
          value={draft}
          onChange={setDraft}
          type="number"
          min={0}
          placeholder={t('platformAdmin.detail.limitPlaceholder')}
          fullWidth
          autoFocus
        />
      </Modal>
    </DetailSection>
  );
}

// ── Módulos ──────────────────────────────────────────────────────────────────

function ModulesSection({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const updateModules = useUpdateAccountModules();
  const [pending, setPending] = useState<{ key: AccountModuleKey; next: boolean } | null>(null);

  const modules = account.modules;

  // El mismo cálculo que hace el backend antes de escribir (blockingDependents
  // vive en @evo-hub/shared) — así el interruptor se ve deshabilitado con el
  // motivo a la vista en vez de dejar hacer clic y devolver un 409. La
  // validación real sigue siendo la del servidor; ésta es para no dejar
  // intentar algo que va a fallar.
  const blockers = useMemo(() => {
    const map = {} as Record<AccountModuleKey, { dependents: AccountModuleKey[]; dependencies: AccountModuleKey[] }>;
    for (const def of ACCOUNT_MODULES) {
      map[def.key] = {
        dependents: modules[def.key] ? blockingDependents(def.key, modules) : [],
        dependencies: modules[def.key] ? [] : missingDependencies(def.key, modules),
      };
    }
    return map;
  }, [modules]);

  async function applyToggle(key: AccountModuleKey, next: boolean) {
    const desired: AccountModules = { ...modules, [key]: next };
    try {
      const result = await updateModules.mutateAsync({ id: account.id, modules: desired });
      toast.success(t('platformAdmin.modules.saved'));
      // Reactivar correo dispara catch-up de lo que entró durante la pausa —
      // se dice explícitamente para que no parezca que se perdió.
      const since = result.reEnabledSince['email'];
      if (result.reEnabled.includes('email') && since) {
        toast.success(t('platformAdmin.modules.reEnabledEmail', { date: fmtDate(since) }));
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  const moduleName = (key: AccountModuleKey) => t(`platformAdmin.modules.${key}.label`);

  const onCount = ACCOUNT_MODULES.filter(def => modules[def.key]).length;

  return (
    <DetailSection
      title={t('platformAdmin.detail.modulesSection')}
      divider={false}
      titleRight={
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
          {t('platformAdmin.detail.modulesOn', { on: onCount, total: ACCOUNT_MODULES.length })}
        </span>
      }
    >
      <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', marginTop: 0, marginBottom: 'var(--evo-space-4)' }}>
        {t('platformAdmin.modules.help')}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(var(--evo-related-col-min), 1fr))', gap: '0 var(--evo-space-6)' }}>
        {ACCOUNT_MODULES.map(def => {
          const enabled = modules[def.key];
          const { dependents, dependencies } = blockers[def.key];
          const blocked = dependents.length > 0 || dependencies.length > 0;
          const blockReason = dependents.length > 0
            ? t('platformAdmin.modules.blockedByDependents', { modules: dependents.map(moduleName).join(', ') })
            : dependencies.length > 0
              ? t('platformAdmin.modules.blockedByDependencies', { modules: dependencies.map(moduleName).join(', ') })
              : null;

          return (
            <div key={def.key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--evo-space-4)', minHeight: 'var(--evo-module-row-min-h)', padding: 'var(--evo-module-row-py) 0', boxSizing: 'border-box' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>{moduleName(def.key)}</div>
                <div style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {t(`platformAdmin.modules.${def.key}.description`)}
                </div>
                {/* El motivo va a la vista, no escondido en un tooltip: Eduardo
                    pidió que el panel diga cuáles módulos lo necesitan, no que
                    lo deje romper la cuenta y explique después. */}
                {blockReason && (
                  <div style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-amber)', marginTop: '2px' }}>
                    {blockReason}
                  </div>
                )}
              </div>
              <Toggle
                checked={enabled}
                onChange={next => setPending({ key: def.key, next })}
                disabled={blocked || updateModules.isPending}
                size="sm"
              />
            </div>
          );
        })}
      </div>

      <Modal
        open={pending != null}
        onClose={() => setPending(null)}
        title={pending
          ? t(pending.next ? 'platformAdmin.modules.enableTitle' : 'platformAdmin.modules.disableTitle', {
              module: moduleName(pending.key), name: account.name,
            })
          : ''}
        size="sm"
        footer={
          <>
            <Button variant="outline" tone="muted" onClick={() => setPending(null)}>{t('common.cancel')}</Button>
            <Button
              variant="solid"
              tone={pending?.next ? 'cyan' : 'amber'}
              loading={updateModules.isPending}
              onClick={() => { if (pending) void applyToggle(pending.key, pending.next); }}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)' }}>
          {pending?.next ? t('platformAdmin.modules.help') : t('platformAdmin.modules.disableWarning')}
        </span>
      </Modal>
    </DetailSection>
  );
}

// ── Usuarios ─────────────────────────────────────────────────────────────────

function UsersSection({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();

  return (
    <DetailSection
      title={t('platformAdmin.detail.usersSection')}
      divider={false}
      titleRight={
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
          {t('platformAdmin.detail.usersCount', { count: account.users.length })}
        </span>
      }
    >
      <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', marginTop: 0, marginBottom: 'var(--evo-space-3)' }}>
        {t('platformAdmin.detail.usersReadOnly')}
      </p>
      {account.users.length === 0 && (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>{t('platformAdmin.detail.noUsers')}</span>
      )}
      {account.users.map(user => (
        <KVRow
          key={user.id}
          label={user.displayName ?? user.email}
          value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--evo-fg-3)' }}>{user.email}</span>
              <Pill size="xs" tone={user.role === 'owner' ? 'cyan' : 'muted'}>{user.role}</Pill>
              {!user.active && <Pill size="xs" tone="danger">{t('platformAdmin.detail.userInactive')}</Pill>}
            </span>
          }
        />
      ))}
    </DetailSection>
  );
}

// ── Zona de riesgo ───────────────────────────────────────────────────────────

function DangerSection({ account }: { account: NonNullable<ReturnType<typeof usePlatformAccount>['data']> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const changeStatus = useChangeAccountStatus();
  const cancelSub = useCancelAccountSubscription();

  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmSlug, setConfirmSlug] = useState('');
  const [stripeWarning, setStripeWarning] = useState(false);

  async function handleClose() {
    try {
      const result = await changeStatus.mutateAsync({ id: account.id, status: 'closed', confirmSlug });
      toast.success(t('platformAdmin.close.done'));
      setClosing(false);
      setConfirmSlug('');
      // Cerrar NO cancela el cobro. Si quedó vivo, se avisa acá mismo en vez de
      // que Eduardo se entere el mes que viene por el resumen de Stripe.
      if (result.stripeSubscriptionStillActive) setStripeWarning(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function handleCancelSubscription() {
    try {
      await cancelSub.mutateAsync({ id: account.id, confirmSlug });
      toast.success(t('platformAdmin.cancelSubscription.done'));
      setCancelling(false);
      setConfirmSlug('');
      setStripeWarning(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  const canReactivate = account.status === 'suspended' || account.status === 'closed';

  // Fila de la zona: acción + su consecuencia ESCRITA al lado, no solo el
  // botón. Separadores en --evo-danger-zone-stroke.
  function DangerRow({ note, action, last = false }: { note: string; action: React.ReactNode; last?: boolean }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-4)', padding: 'var(--evo-space-3) 0', borderBottom: last ? 'none' : '1px solid var(--evo-danger-zone-stroke)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.55 }}>
          {note}
        </span>
        <span style={{ flexShrink: 0 }}>{action}</span>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--evo-danger-zone-stroke)', borderRadius: 'var(--evo-r-md)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 'var(--evo-card-head-pad)', background: 'var(--evo-danger-zone-bg)', borderBottom: '1px solid var(--evo-danger-zone-stroke)' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)', letterSpacing: 'var(--evo-ls-caps)', textTransform: 'uppercase', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
          {t('platformAdmin.detail.dangerSection')}
        </span>
      </div>
      <div style={{ padding: 'var(--evo-card-pad)', display: 'flex', flexDirection: 'column' }}>
      {(stripeWarning || account.hasActiveStripeSubscription) && (
        <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-amber)', marginTop: 0, fontFamily: 'var(--evo-font)' }}>
          {t('platformAdmin.close.stripeStillActive')}
        </p>
      )}

      {canReactivate ? (
        <DangerRow
          note={t('platformAdmin.detail.danger.reactivateNote')}
          action={
            <Button variant="outline" tone="cyan" size="sm" loading={changeStatus.isPending}
              onClick={() => { void changeStatus.mutateAsync({ id: account.id, status: 'active' }); }}>
              {t('platformAdmin.bulk.reactivate')}
            </Button>
          }
        />
      ) : (
        <DangerRow
          note={t('platformAdmin.detail.danger.suspendNote')}
          action={
            <Button variant="outline" tone="amber" size="sm" loading={changeStatus.isPending}
              onClick={() => { void changeStatus.mutateAsync({ id: account.id, status: 'suspended' }); }}>
              {t('platformAdmin.detail.danger.suspend')}
            </Button>
          }
        />
      )}

      {account.stripeSubscriptionId && (
        <DangerRow
          note={t('platformAdmin.detail.danger.cancelNote')}
          action={
            <Button variant="outline" tone="amber" size="sm" onClick={() => { setConfirmSlug(''); setCancelling(true); }}>
              {t('platformAdmin.cancelSubscription.action')}
            </Button>
          }
        />
      )}

      {account.status !== 'closed' && (
        <DangerRow
          last
          note={t('platformAdmin.detail.danger.closeNote')}
          action={
            <Button variant="outline" tone="danger" size="sm" onClick={() => { setConfirmSlug(''); setClosing(true); }}>
              {t('platformAdmin.close.action')}
            </Button>
          }
        />
      )}

      <SlugConfirmModal
        open={closing}
        onClose={() => setClosing(false)}
        title={t('platformAdmin.close.title', { name: account.name })}
        warning={t('platformAdmin.close.warning')}
        confirmLabel={t('platformAdmin.close.confirmLabel')}
        confirmCta={t('platformAdmin.close.confirm')}
        expectedSlug={account.id}
        value={confirmSlug}
        onChange={setConfirmSlug}
        loading={changeStatus.isPending}
        tone="danger"
        onConfirm={() => { void handleClose(); }}
      />

      <SlugConfirmModal
        open={cancelling}
        onClose={() => setCancelling(false)}
        title={t('platformAdmin.cancelSubscription.title', { name: account.name })}
        warning={t('platformAdmin.cancelSubscription.warning')}
        confirmLabel={t('platformAdmin.cancelSubscription.confirmLabel')}
        confirmCta={t('platformAdmin.cancelSubscription.confirm')}
        expectedSlug={account.id}
        value={confirmSlug}
        onChange={setConfirmSlug}
        loading={cancelSub.isPending}
        tone="danger"
        onConfirm={() => { void handleCancelSubscription(); }}
      />
      </div>
    </div>
  );
}

/** "Sin id real no hay confirmación": el botón no se habilita hasta que el
 *  identificador tipeado coincide exacto. Mismo componente para cerrar la
 *  cuenta y para cancelar el cobro — dos acciones distintas con la misma
 *  barrera, para que ninguna quede con una más floja que la otra. */
function SlugConfirmModal({
  open, onClose, title, warning, confirmLabel, confirmCta,
  expectedSlug, value, onChange, loading, onConfirm,
}: {
  open: boolean; onClose: () => void; title: string; warning: string;
  confirmLabel: string; confirmCta: string; expectedSlug: string;
  value: string; onChange: (v: string) => void; loading: boolean;
  tone: 'danger'; onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const matches = value === expectedSlug;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="solid" tone="danger" disabled={!matches} loading={loading} onClick={onConfirm}>
            {confirmCta}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)' }}>{warning}</span>
        <Input
          value={value}
          onChange={onChange}
          label={confirmLabel}
          placeholder={expectedSlug}
          fullWidth
          autoFocus
        />
      </div>
    </Modal>
  );
}
